import styled from "@emotion/styled";
import { useAppDispatch, useAppSelector } from "../store";
import { selectEventState } from "../store/Event/eventStore";
import React from "react";
import { Box, CircularProgress } from "@mui/material";
import { getActiveUsersInMonthly } from "../store/Event/thunks";

import { Sidebar, SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from "../Components/Sidebar/Sidebar";
import { OnboardingPopover } from "../Components/Onboarding/OnboardingPopover";
import { ONBOARDING_STEPS } from "../Components/Onboarding/onboarding.config";

const ONBOARDING_KEY = "onboarding_completed";

// Which sidebar item to highlight / section to expand for each onboarding step
const ONBOARDING_SIDEBAR_PROPS: Record<
  string,
  { highlight: string; expand: "games" | "integrations" | "players" | null }
> = {
  "games-section":        { highlight: "games-section",   expand: "games" },
  "integrations-section": { highlight: "/integrations",   expand: "integrations" },
  "players-section":      { highlight: "players-section", expand: "players" },
  "nav-Analytics":        { highlight: "/events",         expand: null },
};

const Main = styled.main<{ collapsed: boolean }>`
  margin-left: ${({ collapsed }) => (collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH)}px;
  min-height: 100vh;
  padding: 24px 32px 32px;
  transition: margin-left 0.22s ease;
  background: var(--g-bg);
  color: var(--g-text);
`;

const PageLoading = () => (
  <Box sx={{ display: "flex", justifyContent: "center", pt: 10 }}>
    <CircularProgress size={28} />
  </Box>
);

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const { activeUsersLoaded, loadingActiveUsers } = useAppSelector(selectEventState);

  const [collapsed, setCollapsed] = React.useState<boolean>(
    () => localStorage.getItem("sidebar_collapsed") === "true"
  );

  const handleToggle = () => {
    setCollapsed((prev) => {
      localStorage.setItem("sidebar_collapsed", String(!prev));
      return !prev;
    });
  };

  const [onboardingOpen, setOnboardingOpen] = React.useState<boolean>(
    () => localStorage.getItem(ONBOARDING_KEY) !== "true"
  );
  const [onboardingStep, setOnboardingStep] = React.useState(0);

  const finishOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setOnboardingOpen(false);
    setOnboardingStep(0);
  };

  const restartGuide = () => {
    setOnboardingStep(0);
    setOnboardingOpen(true);
    // Expand the sidebar so labels are visible during the guide
    if (collapsed) handleToggle();
  };

  const currentAnchor = onboardingOpen ? ONBOARDING_STEPS[onboardingStep]?.anchor : undefined;
  const guideSidebar = currentAnchor ? ONBOARDING_SIDEBAR_PROPS[currentAnchor] : undefined;

  React.useEffect(() => {
    if (!activeUsersLoaded && !loadingActiveUsers) {
      dispatch(getActiveUsersInMonthly());
    }
  }, [activeUsersLoaded, loadingActiveUsers, dispatch]);

  // Expose current sidebar width so fixed-positioned elements (e.g. pagination bar)
  // can stay within the content area
  React.useEffect(() => {
    document.documentElement.style.setProperty(
      "--g-sidebar-w",
      `${collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}px`
    );
  }, [collapsed]);

  return (
    <>
      <Sidebar
        collapsed={collapsed}
        onToggle={handleToggle}
        onRestartGuide={restartGuide}
        highlightPath={guideSidebar?.highlight}
        expandSection={guideSidebar?.expand}
      />
      <OnboardingPopover
        open={onboardingOpen}
        currentStep={onboardingStep}
        totalSteps={ONBOARDING_STEPS.length}
        onNext={() => setOnboardingStep((s) => s + 1)}
        onClose={finishOnboarding}
        onComplete={finishOnboarding}
      />
      <Main collapsed={collapsed}>
        {/* A page whose code is still loading waits here, inside the layout — not in the app's
            top boundary, which would blank the whole admin, sidebar included. */}
        <React.Suspense fallback={<PageLoading />}>
          {children}
        </React.Suspense>
      </Main>
    </>
  );
};
